import { ContainerModel } from '#lib/ContainerModel.svelte.js';
import ImageEntryModel from '#lib/ImageEntryModel.svelte.js';

import {
  extract_text_teaser_from_container,
  extract_images_from_container
} from '#lib/util.js';

export default class ResourceModel {
  // system-native props
  entry_id = $state();
  type = $state(); // entry type (resource)
  slug = $state();
  created_at = $state();
  updated_at = $state();
  visibility = $state('public');
  view_count = $state();
  preview_title = $state('');
  preview_preamble = $state('');
  preview_description = $state('');
  preview_images_multiref = $state();
  preview_images = $state();

  // resource-specific properties
  title = $state('');
  resource_type = $state(); // meeting_room, office, flex_desk, equipment, addon, open_day
  booking_type = $state(); // daily/half_hourly/subscription
  // Specific to meeting rooms
  lock_ids = $state([]); // List of lock identifiers for access control in Loxone (UUIDs of the "NFC Code Touch" devices configured in the Loxone Miniserver)
  room_id = $state(); // The Loxone identifier for the resource (UUID of the Room in the Loxone Miniserver)
  // Specific to daily bookings
  max_individual_daily_bookings_per_day = $state(); // null or undefined is unlimited
  max_individual_daily_bookings_per_month = $state(); // null or undefined is unlimited

  // Story telling
  body = $state(undefined);
  images_multiref = $state([]); // Holds a reference to image entries
  images = $state([]);

  // derived preview
  preview = $derived(this.to_preview());

  constructor({
    entry_id,
    type,
    slug,
    created_at,
    updated_at,
    visibility,
    view_count,
    preview_title,
    preview_preamble,
    preview_description,
    preview_images_multiref,
    preview_images,
    title,
    resource_type,
    booking_type,
    lock_ids,
    room_id,
    max_individual_daily_bookings_per_day,
    max_individual_daily_bookings_per_month,
    body,
    images_multiref,
    images
  } = {}) {
    // system-native props
    this.entry_id = entry_id;
    this.type = type;
    this.slug = slug;
    this.created_at = created_at;
    this.updated_at = updated_at;
    this.visibility = visibility ?? 'public';
    this.view_count = view_count;
    this.preview_title = preview_title ?? '';
    this.preview_preamble = preview_preamble ?? '';
    this.preview_description = preview_description ?? '';
    this.preview_images_multiref = preview_images_multiref ?? [];
    this.preview_images = preview_images?.map(i => new ImageEntryModel(i)) ?? [];

    // resource-specific properties
    this.title = title ?? '';
    this.resource_type = resource_type;
    this.booking_type = booking_type;
    // Specific to meeting rooms
    this.lock_ids = lock_ids ?? [];
    this.room_id = room_id;
    // Specific to daily bookings
    this.max_individual_daily_bookings_per_day = max_individual_daily_bookings_per_day;
    this.max_individual_daily_bookings_per_month = max_individual_daily_bookings_per_month;
    // Configure container with allowed block types
    this.body = new ContainerModel({
      blocks: body || [],
      block_types: ['story'],
      default_block_type: 'story'
    }, this);
    this.images_multiref = images_multiref ? JSON.parse(JSON.stringify(images_multiref)) : [];
    // If images are resolved server-side, use them to avoid flash of content on page load.
    if (images?.length > 0) {
      this.images = $state.snapshot(images);
    } else {
      this.images = [];
    }
  }


  to_preview() {
    return {
      entry_id: this.entry_id,
      type: this.type,
      slug: this.slug,
      preamble: this.preview_preamble,
      title: this.preview_title || this.title,
      description: this.preview_description || extract_text_teaser_from_container(this.body),
      images: this.preview_images?.length > 0
        ? this.preview_images.map(i => i.to_json())
        : (() => {
            const main_images = this.images.length > 0 ? $state.snapshot(this.images.slice(0, 3)) : [];
            const container_images = main_images.length < 3 
              ? extract_images_from_container(this.body).slice(0, 3 - main_images.length)
              : [];
            return [...main_images, ...container_images];
          })(),
      created_at: this.created_at,
      updated_at: this.updated_at,
      visibility: this.visibility,
      // Resource-specific preview info
      resource_type: this.resource_type,
      booking_type: this.booking_type,
    };
  }

  to_plain_text() {
    return [
      this.title,
      this.preview_title,
      this.preview_description,
      extract_text_teaser_from_container(this.body)
    ].filter(Boolean).join(' ');
  }

  to_json() {
    return {
      entry_id: this.entry_id,
      type: this.type,
      visibility: this.visibility,
      preview_title: this.preview_title,
      preview_preamble: this.preview_preamble,
      preview_description: this.preview_description,
      preview_images_multiref: this.preview_images_multiref,
      title: this.title,
      resource_type: this.resource_type,
      booking_type: this.booking_type,
      lock_ids: this.lock_ids,
      room_id: this.room_id,
      max_individual_daily_bookings_per_day: this.max_individual_daily_bookings_per_day,
      max_individual_daily_bookings_per_month: this.max_individual_daily_bookings_per_month,
      body: this.body.to_json(),
      images_multiref: this.images_multiref,
    };
  }

  toJSON() {
    return this.to_json();
  }

  static SORTED_RESOURCE_TYPE_OPTIONS = [
    'meeting_room',
    'flex_desk',
    'fixed_desk',
    'office',
    'open_day',
    'equipment',
    'addon',
  ];

  static group_by_type(resources) {
    const grouped = resources.reduce((grouped, resource) => {
      const type = resource.resource_type;
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(resource);
      return grouped;
    }, {});
    
    // Sort resources within each group by title
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => a.title.localeCompare(b.title, undefined, {numeric: true}));
    });
    
    // Convert to ordered Map based on SORTED_RESOURCE_TYPE_OPTIONS
    const ordered_groups = new Map();
    ResourceModel.SORTED_RESOURCE_TYPE_OPTIONS.forEach(type => {
      if (grouped[type]) {
        ordered_groups.set(type, grouped[type]);
      }
    });
    
    return ordered_groups;
  }

  is_new() {
    return !this.entry_id;
  }
}